import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Shield } from 'lucide-react';
import { useCoveragePage } from '@/hooks/useCoveragePage';
import {
  CoveragePageHeader,
  PlanSummaryCard,
  BenefitSectionAccordion,
} from '@/components/insurance/coverage';

function CoveragePageSkeleton() {
  return (
    <div className="min-h-full bg-background-secondary">
      <div className="bg-card border-b border-border px-8 py-5">
        <Skeleton className="h-8 w-48" />
      </div>
      <div className="px-8 py-6 space-y-6">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    </div>
  );
}

export function MyCoveragePage() {
  const { policyId } = useParams<{ policyId: string }>();
  const navigate = useNavigate();
  const data = useCoveragePage(policyId);

  if (!policyId) {
    return (
      <div className="min-h-full bg-background-secondary flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-10 h-10 text-foreground-muted mb-3" />
        <p className="text-sm text-muted-foreground">Policy not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(data.basePath)}>
          Back to My Insurance
        </Button>
      </div>
    );
  }

  if (data.isLoading) {
    return <CoveragePageSkeleton />;
  }

  if (data.hasNoPlan) {
    return (
      <div className="min-h-full bg-background-secondary">
        <CoveragePageHeader
          basePath={data.basePath}
          planName={data.planName}
          onDownloadCertificate={data.handleDownloadCertificate}
          canDownload={data.canDownload}
        />
        <div className="flex flex-col items-center justify-center py-20">
          <Shield className="w-10 h-10 text-foreground-muted mb-3" />
          <h3 className="text-base font-medium text-foreground mb-1">No plan assigned</h3>
          <p className="text-sm text-muted-foreground">
            This policy has no benefit plan linked yet.
          </p>
        </div>
      </div>
    );
  }

  const { plan } = data;
  if (!plan) return null;

  return (
    <div className="min-h-full bg-background-secondary">
      <CoveragePageHeader
        basePath={data.basePath}
        planName={data.planName}
        onDownloadCertificate={data.handleDownloadCertificate}
        canDownload={data.canDownload}
      />

      <div className="px-8 py-6 space-y-6">
        <PlanSummaryCard
          planName={plan.name}
          status={plan.status}
          maxAnnualBenefit={plan.maxAnnualBenefit}
          annualDeductible={plan.annualDeductible}
          coinsurancePlanPays={plan.coinsurancePlanPays}
          coinsuranceYouPay={plan.coinsuranceYouPay}
          areaOfCoverage={plan.areaOfCoverage}
          currency={plan.currency}
        />

        <h2 className="text-lg font-semibold text-foreground">Coverage Items</h2>

        <BenefitSectionAccordion
          sections={plan.sections}
          utilizationByItemId={data.utilizationByItemId}
          currency={plan.currency}
          planCoinsurance={plan.coinsurancePlanPays}
        />
      </div>
    </div>
  );
}
